/* stylelint-disable unit-no-unknown */
import {memo, useEffect, useState} from 'react';

import {times} from 'lodash';
import styled from 'styled-components';
import {useDispatch} from 'react-redux';

import {Dish} from '~/shared/store/models';
import ArrowLeft from '~/assets/images/svgIconsAsText/back-arrow.svg?react';
import Button from '~/shared/components/Button';
import {body14Bold, body14Normal, body18Bold, body20Bold} from '~/shared/theme/typography';
import {SkeletonLoader, Skeleton} from '~/shared/components/Loaders';
import {DishAdditionalInfoResponse} from '~/shared/services/bffService/api/interfaces/responses';
import {fetchItemAdditionalInfo} from '~/shared/services/bffService/api';
import {is401Error} from '~/shared/services/apiErrorService';
import {handleRefreshToken} from '~/shared/services/auth/auth';
import infoIcon from '~/assets/images/icons/infoIcon.svg';
import ImageWithAlt from '~/shared/components/ImageWithAlt';
import {flipOnLTR, media, rotateOnRTL} from '~/shared/theme/utils';
import {CloseButtonSVGIcon} from '~/shared/components/styled';
import {CloseButton} from '~/shared/components/Modals';
import {setCurrentModalArgs} from '~/shared/store/actions';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';
import {flexCenter, flexColumn} from '~/shared/theme/FlexLayout';

import {DishType} from '../../modals/DishType';

const Wrapper = styled.div`
  color: ${({theme}) => theme.colors.secondary};
  height: calc(100dvh - 116px - 20px);
  overflow: auto;
  ${media.minTablet`
    overflow: visible;
    height: auto;
    margin-bottom: 0;
  `}
`;

const StyledArrowLeft = styled(ArrowLeft)`
  scale: 1.3;
  ${rotateOnRTL}
`;

const BackButton = styled(Button)`
  ${flexCenter}
`;

const Header = styled.header`
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  ${body20Bold}
  margin-top: 7px;
  ${media.minTablet`
    padding-top: 20px;
  `}
`;

const DishName = styled.span`
  ${flipOnLTR`
    margin-left: 40px;  
  `}
`;

const Main = styled.div`
  padding: 0 20px;
  direction: rtl;
  text-align: right;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${({theme}) => theme.colors.gray800};
  gap: 16px;
  ${flexColumn}
  margin-bottom: 20px;
`;

const ContentWrapper = styled.div`
  ${flexColumn}
  gap: 16px;
`;

const Title = styled.h3`
  ${body18Bold}
`;

const Content = styled.p`
  ${body14Normal}
`;

const Disclaimer = styled.div`
  ${body14Bold}
`;

const InfoWrapper = styled.div`
  display: flex;
  align-items: flex-start;
`;

const InfoIcon = styled(ImageWithAlt).attrs({src: infoIcon, noAlt: true})`
  height: 12px;
  margin-top: 7px;
  margin-left: 4px;
`;

const StyledCloseButton = styled(CloseButton)`
  background-color: ${({theme}) => theme.colors.gray920};
  top: 0;
  ${flipOnLTR`
    left: 16px;
  `}
`;

const Skeletons = () => {
  return (
    <>
      {times(5, index => (
        <div key={index}>
          <Skeleton height={24} width={150} />
          <Skeleton height={16} style={{marginTop: 12}} />
        </div>
      ))}
      <Skeleton height={16} style={{marginTop: 12}} />
      <Skeleton height={16} style={{marginTop: 0}} />
      <Skeleton height={16} style={{marginTop: 0}} />
    </>
  );
};

const useHideModalsCloseButton = () => {
  const dispatch = useDispatch();
  const isMinLargeMobile = useIsMinLargeMobile();

  useEffect(() => {
    if (isMinLargeMobile) {
      return;
    }

    dispatch(setCurrentModalArgs({hideCloseButton: true}));
    return () => {
      dispatch(setCurrentModalArgs({hideCloseButton: false}));
    };
  }, [dispatch, isMinLargeMobile]);

};

const useAdditionalInfo = ({itemId}: {itemId: DishType['dishId']}) => {
  const [data, setData] = useState<DishAdditionalInfoResponse | null>(null);

  useHideModalsCloseButton();

  useEffect(() => {
    const fetchAdditionalInfo = async () => {
      const additionalInfo = await fetchItemAdditionalInfo(itemId);
      setData(additionalInfo);
    };

    (async () => {
      try {
        await fetchAdditionalInfo();
      } catch (error) {
        setData({} as DishAdditionalInfoResponse);
        if (is401Error(error)) {
          await handleRefreshToken(error, () => fetchAdditionalInfo());
        }
      }
    })();
  }, [itemId]);
  
  return {
    data,
    isLoading: Boolean(!data),
  };
};

const AdditionalInfo = ({
  dishName,
  withBackButton,
  onBackClick,
  itemId,
  onClose,
}: {
  dishName: Dish['dishName'];
  withBackButton?: boolean;
  onBackClick?: () => void;
  itemId: Dish['dishId'];
  onClose?: () => void;
}) => {
  const {
    data: additionalInfo,
    isLoading: isAdditionalInfoLoading,
  } = useAdditionalInfo({itemId});

  return (
    <Wrapper>
      <Header>
        {withBackButton && <BackButton onClick={onBackClick}><StyledArrowLeft /></BackButton>}
        <DishName id="modal-title">{dishName}</DishName>
        {onClose && <StyledCloseButton onClick={onClose}><CloseButtonSVGIcon /></StyledCloseButton>}
      </Header>
      <Main>
        <SkeletonLoader shouldShowLoader={isAdditionalInfoLoading} LoaderComponent={Skeletons}>
          <ContentWrapper>
            {additionalInfo?.sections?.map(({title, content}) => (
              <div key={title}>
                <Title>{title}</Title>
                <Content>{content}</Content>
              </div>
            ))}
            <InfoWrapper>
              <InfoIcon />
              <Disclaimer>הנתונים המדויקים מופיעים על גבי המוצר, אין להסתמך על הפירוט המופיע באתר, יתכנו טעויות או אי התאמות, יש לקרוא את המופיע על גבי אריזת המוצר לפני השימוש. התמונות והתאריכים המופיעים הינם להמחשה בלבד ואין להסתמך עליהם.</Disclaimer>
            </InfoWrapper>
          </ContentWrapper>
        </SkeletonLoader>
      </Main>
    </Wrapper>
  );
};

export default memo(AdditionalInfo);
